import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "dark" | "light";

interface UiState {
  theme: Theme;
  selectedDeviceId: string | null;
  drawerOpen: boolean;
  pairModalOpen: boolean;
  activeCompanyId: string | null;
  /**
   * The user's currently-selected workspace folder per device. Used by the
   * Workspaces panel (Device Detail) and the folder-picker chip on the chat
   * panel to stay in sync. `null` / missing entry → "no folder picked, use
   * the device's default cwd". Persisted across reloads.
   */
  selectedWorkspaceByDevice: Record<string, string>;

  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  setSelectedDeviceId: (id: string | null) => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  setPairModalOpen: (open: boolean) => void;
  setActiveCompanyId: (id: string | null) => void;
  setSelectedWorkspace: (deviceId: string, path: string | null) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: "dark",
      selectedDeviceId: null,
      drawerOpen: false,
      pairModalOpen: false,
      activeCompanyId: null,
      selectedWorkspaceByDevice: {},

      setTheme: (theme) => {
        document.documentElement.dataset.theme = theme;
        set({ theme });
      },
      toggleTheme: () =>
        set((s) => {
          const next: Theme = s.theme === "dark" ? "light" : "dark";
          document.documentElement.dataset.theme = next;
          return { theme: next };
        }),
      setSelectedDeviceId: (selectedDeviceId) => set({ selectedDeviceId }),
      openDrawer: () => set({ drawerOpen: true }),
      closeDrawer: () => set({ drawerOpen: false }),
      setPairModalOpen: (open) => set({ pairModalOpen: open }),
      setActiveCompanyId: (activeCompanyId) => set({ activeCompanyId }),
      setSelectedWorkspace: (deviceId, path) =>
        set((s) => {
          const next = { ...s.selectedWorkspaceByDevice };
          if (path === null) delete next[deviceId];
          else next[deviceId] = path;
          return { selectedWorkspaceByDevice: next };
        }),
    }),
    {
      name: "pcc-ui",
      partialize: (s) => ({
        theme: s.theme,
        activeCompanyId: s.activeCompanyId,
        selectedWorkspaceByDevice: s.selectedWorkspaceByDevice,
      }),
    },
  ),
);

/** Apply persisted theme on bootstrap. Idempotent. */
export function applyPersistedTheme(): void {
  const t = useUiStore.getState().theme;
  document.documentElement.dataset.theme = t;
}
